import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, AlertTitle } from '@mui/material';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { workspaceValidateLink, saveWorkspace } from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  ControlledTextInput,
  Item,
  WsButton,
  Notification,
  IUserDTO,
} from 'wisestep-base';
import { object, string } from 'yup';

import { AuthLayout } from '../../../components';
import { useUserContext } from '../../../context';
import ISaveWorkspaceParam from '../types/ISaveWorkspaceParam';

interface IWorkspaceForm {
  workspaceName: string;
}

const validationSchema = object().shape({
  workspaceName: string().required('Name is required'),
});

const CreateWorkspace = () => {
  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: {
      workspaceName: '',
    },
  };

  const navigate = useNavigate();

  const user = useUserContext().userDTO as IUserDTO;

  const [notify, setNotify] = useState<boolean>(false);

  const [notificationObject, setNotificationObject] = useState<{
    message: string;
    type: 'error' | 'info' | 'success' | 'warning';
  }>();

  const methods = useForm<IWorkspaceForm>(formOptions);

  const { handleSubmit, watch } = methods;

  const workspaceLink = watch('workspaceName');

  const onSubmit = async (data: FieldValues) => {
    if (data && data.workspaceName && data.workspaceName !== '') {
      await workspaceValidateLink({ link: data.workspaceName });
      const workspaceObj: ISaveWorkspaceParam = {
        onboardingQuestionAnswered: false,
        link: data.workspaceName?.trim()?.toLowerCase()?.split(/\s+/)?.join('-'),
        name: data.workspaceName.toLowerCase(),
        domains: user['email'] ? [user['email'].split('@')[1]] : [],
        phoneNumber: user.phoneNumber ? user.phoneNumber : '',
      };
      try {
        await saveWorkspace(workspaceObj);
        setNotify(true);
        setNotificationObject({
          message: 'Workspace created successfully.',
          type: 'success',
        });
        navigate('/select-workspace');
      } catch (e) {
        setNotify(true);
        setNotificationObject({
          message: 'Some error occurred. Please try later',
          type: 'error',
        });
      }
    }
  };

  return (
    <AuthLayout>
      <Item>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid item xs={12}>
              <Item>
                <Stack mt={1} alignItems="center">
                  <WsTypography variant={WsTypoVariants.TITLE}>Create workspace</WsTypography>
                </Stack>
                <Stack mt={2} direction="column" spacing={2} alignItems="center">
                  <WsTypography color={WsTypoColors.QUATERNARY} variant={WsTypoVariants.BODY1}>
                    Enter the workspace name for the domain
                  </WsTypography>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>‘genesis.uk’</WsTypography>
                </Stack>
                <Stack mt={2} direction="column" spacing={2}>
                  <ControlledTextInput
                    name={'workspaceName'}
                    labelText="Workspace Name *"
                    fullWidth
                    id="workspace-name"
                    placeholder="Eg, Genesis UK"
                    variant="outlined"
                    required
                    autoComplete="workspaceName"
                  />
                </Stack>
                {!isEmpty(workspaceLink) && (
                  <Stack mt={2} alignItems="center">
                    <Alert severity="info">
                      <AlertTitle>Your Wisestep url will be</AlertTitle>
                      {`${workspaceLink
                        ?.trim()
                        ?.toLowerCase()
                        ?.split(/\s+/)
                        ?.join('-')}.wisestep.com`}
                    </Alert>
                  </Stack>
                )}
                <Stack mt={2}>
                  <WsButton variant="contained" size="large" fullWidth type="submit">
                    Continue
                  </WsButton>
                </Stack>
                <Notification
                  vertical="bottom"
                  horizontal="center"
                  autoHideDuration={6000}
                  notify={notify}
                  notificationObject={notificationObject}
                />
              </Item>
            </Grid>
          </form>
        </FormProvider>
      </Item>
    </AuthLayout>
  );
};

export default CreateWorkspace;
